// Moved from `src/hooks/mutation/useMutateToggleTeamReviewConfirmation.ts` (now
// the app delegate) with the OT4 mutation slice (ARCFE-1033).
//
// Severed per ARCFE-921: both toasts and `invalidateCurrentUser()` (see the
// sibling consent hook for why that crosses as a port). No Rematch dispatch on
// this one — it is the thinnest of the four team-setting toggles.
import { logger } from '@heidi/runtime/logger';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { GET_TEAM_DETAILS } from '../../contracts';
import { toggleTeamReviewConfirmation } from '../api/teams';

export interface ToggleTeamReviewConfirmationVariables {
  teamId: string;
  reviewConfirmationEnabled: boolean;
}

export interface UseMutateToggleTeamReviewConfirmationOptions {
  /** App-reaction port: fired where `invalidateCurrentUser()` fired. */
  onCurrentUserRefreshNeeded?: () => void;
  /** App-reaction port: fired where the legacy success toast fired. */
  onReviewConfirmationUpdated?: () => void;
  /** App-reaction port: fired where the legacy destructive toast fired. */
  onReviewConfirmationUpdateError?: (error: Error) => void;
}

/** PATCH /teams/{team_id}/review-confirmation */
export function useMutateToggleTeamReviewConfirmation(
  options?: UseMutateToggleTeamReviewConfirmationOptions
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['toggleTeamReviewConfirmation'],
    mutationFn: async (input: ToggleTeamReviewConfirmationVariables) => {
      await toggleTeamReviewConfirmation({
        teamId: input.teamId,
        reviewConfirmationEnabled: input.reviewConfirmationEnabled,
      });
    },
    onSuccess: async (_, input) => {
      await queryClient.invalidateQueries({
        queryKey: [GET_TEAM_DETAILS, { teamId: input.teamId }],
      });

      options?.onCurrentUserRefreshNeeded?.();

      options?.onReviewConfirmationUpdated?.();
    },
    onError: (error) => {
      logger.error('Error updating review confirmation settings:', error);
      options?.onReviewConfirmationUpdateError?.(error);
    },
  });
}
